"use client"

import * as React from "react"
import { EyeIcon, MailIcon, MoreHorizontalIcon, PencilIcon } from "lucide-react"

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  cn,
} from "@ibada/ui"

import { TeamPanelViewPills } from "@/features/users/components/team-panel"
import { type TableToolbarViews } from "@/features/users/components/users-table"
import { type WorkspaceInvitation } from "@/features/users/model"
import { InvitationListStatus } from "@/features/users/model/team-panel"
import { useLocale, useTranslations } from "@/i18n"
import { AliveMark, AlivePillTone, AliveStatusCell, formatAliveRelativeDate } from "@/shared/components/alive-cells"

const STATUS_TONE: Record<string, AlivePillTone> = {
  [InvitationListStatus.Pending]: AlivePillTone.Warning,
  [InvitationListStatus.Accepted]: AlivePillTone.Success,
  [InvitationListStatus.Declined]: AlivePillTone.Danger,
  [InvitationListStatus.Revoked]: AlivePillTone.Neutral,
  [InvitationListStatus.Expired]: AlivePillTone.Neutral,
}

function statusOf(invitation: WorkspaceInvitation) {
  return invitation.status.trim().toLowerCase()
}

/**
 * The Invitations view of the desktop team panel (board M4): one row per invitation (email, role, when it was
 * sent) with Renvoyer and Annuler on the ones still waiting. The answered ones (accepted, declined, revoked,
 * expired) stay one click away rather than mixed into what still needs doing.
 */
export function TeamInvitationsPanel({
  invitations,
  isLoading,
  views,
  canManage,
  onResend,
  onRevoke,
  onViewDetails,
  onEditRoles,
}: {
  invitations: WorkspaceInvitation[]
  isLoading: boolean
  views?: TableToolbarViews
  canManage: boolean
  onResend: (invitation: WorkspaceInvitation) => void
  onRevoke: (invitation: WorkspaceInvitation) => void
  onViewDetails: (invitation: WorkspaceInvitation) => void
  onEditRoles: (invitation: WorkspaceInvitation) => void
}) {
  const t = useTranslations("users.panel.invitations")
  const tInvitations = useTranslations("users.invitations")
  const tInvite = useTranslations("users.inviteModal")
  const locale = useLocale()
  const [showPast, setShowPast] = React.useState(false)

  const sorted = React.useMemo(
    () => [...invitations].sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? ""))),
    [invitations],
  )
  const pending = sorted.filter((invitation) => statusOf(invitation) === InvitationListStatus.Pending)
  const past = sorted.filter((invitation) => statusOf(invitation) !== InvitationListStatus.Pending)
  const visible = showPast ? [...pending, ...past] : pending

  const sentLabel = (invitation: WorkspaceInvitation) => {
    const date = invitation.createdAt ? new Date(invitation.createdAt) : null
    if (!date || Number.isNaN(date.getTime())) return null
    return t("sent", { when: formatAliveRelativeDate(date, locale) })
  }

  return (
    <div className='bg-card flex flex-col gap-0.5 rounded-2xl border p-2.5'>
      {views ? <TeamPanelViewPills views={views} /> : null}
      {isLoading ? (
        <div className='flex flex-col gap-2 p-1.5' aria-busy='true'>
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className='h-10 w-full rounded-[14px]' />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <p className='text-muted-foreground px-2.5 py-6 text-center text-sm'>{t("empty")}</p>
      ) : (
        <ul className='divide-border/60 flex flex-col divide-y'>
          {visible.map((invitation) => {
            const status = statusOf(invitation)
            const isPending = status === InvitationListStatus.Pending
            const manageable = isPending && canManage
            const sent = sentLabel(invitation)
            return (
              <li key={invitation.id} className={cn("flex items-center gap-3 px-2.5 py-2.5", !isPending && "opacity-80")}>
                <AliveMark icon={MailIcon} />
                <span className='flex min-w-0 flex-1 flex-col'>
                  <button
                    type='button'
                    onClick={() => onViewDetails(invitation)}
                    className='truncate text-left text-[13.5px] font-bold hover:underline focus-visible:underline focus-visible:outline-none'
                  >
                    {invitation.email || "-"}
                  </button>
                  {sent ? <span className='text-muted-foreground truncate text-xs'>{sent}</span> : null}
                </span>
                {invitation.roleKey ? (
                  <AliveStatusCell tone={AlivePillTone.Neutral}>{tInvite(`roles.${invitation.roleKey}` as "roles.USER")}</AliveStatusCell>
                ) : null}
                {!isPending ? (
                  <AliveStatusCell tone={STATUS_TONE[status] ?? AlivePillTone.Neutral}>
                    {STATUS_TONE[status] ? tInvitations(`statuses.${status}` as never) : invitation.status}
                  </AliveStatusCell>
                ) : null}
                {manageable ? (
                  <span className='flex items-center gap-1.5'>
                    <Button variant='outline' size='sm' className='rounded-full' onClick={() => onResend(invitation)}>
                      {t("resend")}
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='text-destructive hover:text-destructive rounded-full'
                      onClick={() => onRevoke(invitation)}
                    >
                      {t("cancel")}
                    </Button>
                  </span>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant='ghost' size='icon-sm' className='rounded-full'>
                      <span className='sr-only'>{tInvitations("actions.openMenu")}</span>
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <DropdownMenuItem onSelect={() => onViewDetails(invitation)}>
                      <EyeIcon />
                      {tInvitations("actions.viewDetails")}
                    </DropdownMenuItem>
                    {manageable ? (
                      <DropdownMenuItem onSelect={() => onEditRoles(invitation)}>
                        <PencilIcon />
                        {tInvitations("actions.editRoles")}
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            )
          })}
        </ul>
      )}
      {!isLoading && past.length > 0 ? (
        <button
          type='button'
          onClick={() => setShowPast((current) => !current)}
          className='text-muted-foreground hover:text-foreground self-start rounded-full px-2.5 py-1.5 text-xs font-bold'
        >
          {showPast ? t("hidePast") : t("showPast", { count: past.length })}
        </button>
      ) : null}
    </div>
  )
}
